import { type FC, Fragment } from "react";
import { Icon } from "@root/shared/components/Icon";
import { FeatureStatusLabel } from "@root/clientFrontend/features/scout/components/FeatureStatusLabel";
import { Button } from "@root/shared/components/Button";
import { InformationBox } from "@clientFrontend/features/scout/components/InformationBox";
import { type UserInformation } from "@clientFrontend/features/scout/types";
import { Colors } from "@root/shared/utils/colors";
import { Checkbox } from "@root/shared/components/Checkbox";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { UserDetailModalSp } from "@clientFrontend/features/scout/components/UserDetailModalSp";
import { PopOver } from "@root/shared/components/PopOver";
import { Label } from "@root/shared/components/Label";
import * as styles from "./styles.css";

type Props = {
  userInformation: UserInformation;
  userStatus?: {
    userId: string;
    isChecked: boolean;
  };
  handleChangeCheckbox?: (value: string) => void;
  isShadow?: boolean;
  isShowScoutNavigation: boolean;
  isShowCheckbox?: boolean;
  isDisabled?: boolean;
  isShow: boolean;
  onClick: () => void;
  onClose: () => void;
  isShowPopOver?: boolean;
  handleClickPopOver?: () => void;
  isHiddenInitialValue?: boolean;
  hiddenScoutUrl?: string;
};

type InfoListItemProps = {
  title: string;
  children: React.ReactNode;
};

const InfoListItem: FC<InfoListItemProps> = ({ title, children }) => (
  <dl className={styles.dl}>
    <dt className={styles.dt}>{title}：</dt>
    <dd className={styles.dd}>{children}</dd>
  </dl>
);

export const Presentation: FC<Props> = ({
  userInformation: {
    gaEvents,
    isIconFire,
    title,
    labels,
    actionInfo,
    hopedJobTypes,
    hopedEmploymentPatterns,
    hopedCities,
    address,
    isNationalQualificationsHopeJobType,
    acceptableReceivedScoutCount,
    multiActionInfo,
    userDetailFetchUrl,
  },
  userStatus,
  handleChangeCheckbox,
  isShadow,
  isShowScoutNavigation,
  isShowCheckbox = true,
  isDisabled = false,
  isShow,
  onClick,
  onClose,
  isHiddenInitialValue,
  hiddenScoutUrl,
  isShowPopOver,
  handleClickPopOver,
}) => {
  return (
    <div
      className={styles.cassette}
      style={assignInlineVars({
        [styles.boxShadow]: isShadow ? "0px 2px 4px 0px rgba(0, 0, 0, 0.10)" : "none",
      })}
    >
      <div className={styles.cassetteHeader}>
        <div className={styles.cassetteHeaderContent}>
          <div className={styles.titleOuter}>
            {isIconFire && (
              <span className={styles.fireIcon}>
                <Icon name="fire" color={`${Colors.ALERT}`} fontSize="16px" />
              </span>
            )}
            <h3 className={styles.title}>{title}</h3>
          </div>
          {labels && (
            <>
              {labels.map((label) => (
                <span key={label} className={styles.labelOuter}>
                  <FeatureStatusLabel featureStatus={label} />
                </span>
              ))}
            </>
          )}
        </div>
        {userStatus && isShowCheckbox && (
          <Checkbox
            value={userStatus.userId}
            checked={userStatus.isChecked}
            onChange={(event) => handleChangeCheckbox && handleChangeCheckbox(event.target.value)}
            disabled={isDisabled}
            // userId取得のために必要(React外のjavaScript)
            className="js-scout-user-id"
          />
        )}
      </div>
      <div className={styles.dlContainer}>
        {isShowPopOver && (
          <PopOver arrowPosition="bottomLeft" top="-8px">
            <div className={styles.popOverContents}>
              <Label theme="lightPink">推奨</Label>
              <div className={styles.popOverTextWrapper}>
                <span className={styles.popOverBoldText}>求人の職種・役職とマッチしている</span>
                求職者です
              </div>
            </div>
          </PopOver>
        )}
        <InfoListItem title={actionInfo.name}>{actionInfo.date}</InfoListItem>
        {hopedJobTypes && (
          <InfoListItem title="希望職種">
            {hopedJobTypes.map((job, index) => (
              <Fragment key={job.businessTypeName}>
                {job.businessTypeName}
                {job.hasRecommendedLabel && (
                  <Label theme="lightPink">
                    <button
                      className={styles.labelButton}
                      type="button"
                      onClick={handleClickPopOver}
                    >
                      推奨
                    </button>
                  </Label>
                )}
                {index < hopedJobTypes.length - 1 && " / "}
              </Fragment>
            ))}
          </InfoListItem>
        )}
        {hopedEmploymentPatterns && (
          <InfoListItem title="希望雇用形態">{hopedEmploymentPatterns}</InfoListItem>
        )}
        {hopedCities && <InfoListItem title="希望勤務地">{hopedCities}</InfoListItem>}
        {address && <InfoListItem title="住所">{address}</InfoListItem>}
        <InfoListItem title="資格区分">
          {isNationalQualificationsHopeJobType ? "国家" : "民間"}
        </InfoListItem>
        <InfoListItem title="スカウト受信数">{String(acceptableReceivedScoutCount)}</InfoListItem>
        {multiActionInfo?.map(({ works, actionText, isHot }) => (
          <InformationBox key={`${actionText}_${works}`} className={styles.informationBox}>
            <b>
              {actionText}
              {works.length > 1 && (
                <span className={styles.lengthText}>※他{works.length - 1}件</span>
              )}
            </b>
            <br />
            <p className={styles.actionInfo}>
              {works.map((infoText, index) => (
                <Fragment key={infoText}>
                  {index === 0 && (
                    <>
                      {isHot && (
                        <i className={`c-icn c-icn-fire ${styles.informationBoxFireIcon}`} />
                      )}
                      {infoText}
                    </>
                  )}
                </Fragment>
              ))}
            </p>
          </InformationBox>
        ))}
      </div>
      <div className={styles.buttonOuter}>
        <Button
          theme="tertiary"
          onClick={onClick}
          className={styles.linkButton}
          data-ga-action={gaEvents?.detailLink.gaAction}
          data-ga-label={gaEvents?.detailLink.gaLabel}
        >
          プロフィールの詳細を見る
        </Button>
      </div>
      {isShow && (
        <UserDetailModalSp
          fetchUrl={userDetailFetchUrl}
          isShow={isShow}
          onClose={onClose}
          isShowScoutNavigation={isShowScoutNavigation}
          isHiddenInitialValue={isHiddenInitialValue}
          hiddenScoutUrl={hiddenScoutUrl}
          userId={userStatus?.userId}
        />
      )}
    </div>
  );
};
